import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, List, Typography } from '@mui/material';

import { AuthStoreContext } from '../../Root';
import KosaStore from '../../../stores/kosa-store';
import KosaView from './KosaView';
import KosaSkeleton from './KosaSkeleton';
import { Selection } from '../selection';

//=================================================================================================
function KosaListSkeleton() {
    return (
        <>
            {[0, 1, 2, 3].map((i) => (
                <KosaSkeleton key={i} />
            ))}
        </>
    );
}

function KosaLoadError() {
    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error">
                Ошибка загрузки кос
            </Typography>
        </Box>
    );
}

function KosaEmpty() {
    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
                Косы не найдены
            </Typography>
        </Box>
    );
}

//=================================================================================================
interface KosaLayoutProps {
    selection?: Selection;
    onSelect?: (sel: Selection) => void;
}

export const KosaLayout = observer(({ selection, onSelect }: KosaLayoutProps) => {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new KosaStore(user?.node_id ?? 0));
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!user) return;
        store.load().catch(() => setError(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    let content: React.ReactNode;
    if (store.loading) {
        content = <KosaListSkeleton />;
    } else if (error) {
        content = <KosaLoadError />;
    } else if (store.items.length === 0) {
        content = <KosaEmpty />;
    } else {
        content = store.items.map((k) => (
            <KosaView
                key={k.id}
                id={k.id}
                sensorCount={k.sensorCount}
                lastTransaction={k.lastTransaction}
                selected={selection?.kind === 'kosa' && selection.kosaId === k.id}
                onClick={(kosaId) => onSelect?.({ kind: 'kosa', kosaId })}
            />
        ));
    }

    return (
        <List dense disablePadding>
            {content}
        </List>
    );
});

export default KosaLayout;
